import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Container, Jumbo, Tabs, tokens } from '@treams/treams-ui';

import GoalDriverSettingsContainer from '../packages/kakapo-ui-application/pages/goals/create/GoalDriverSettingsContainer';
import AISettingsContainer from './ai/AISettingsContainer';
import CreateDepartment from './departments/CreateDepartment';
import Department from './departments/Department';
import OrganizationDepartments from './departments/OrganizationDepartments';
import SettingsCreateNewTeam from './teams/SettingsCreateNewTeam';
import SettingsEditTeamID from './teams/SettingsEditTeam';
import SettingsTeams from './teams/SettingsTeams';
import SettingsThemeContainer from './theme/SettingsThemeContainer';

export default function OrganizationSettings() {
	const { t } = useTranslation();
	const { canViewAndEditAllSettings, isHRBusinessPartner } = useViewerRoles();

	const hasGoals = useFeature(graphql.FeatureName.Goals);

	return (
		<>
			<Jumbo
				title={t('settings.organization.title')}
				description={t('settings.organization.description')}
				footer={
					<Tabs.Container mb="-1px">
						<Tabs.Item
							title={t('settings.organizations.departments.title')}
							path="/settings/organization/departments"
							exact={false}
						/>
						<Tabs.Item
							title={t('settings.organizations.teams.title')}
							path="/settings/organization/teams"
							exact={false}
						/>

						{canViewAndEditAllSettings && (
							<Tabs.Item
								title={t('settings.organizations.theme.title')}
								path="/settings/organization/theme"
							/>
						)}
						{(canViewAndEditAllSettings || isHRBusinessPartner) && hasGoals && (
							<Tabs.Item
								title={t('settings.organizations.drivers.title')}
								path="/settings/organization/drivers"
							/>
						)}
						{canViewAndEditAllSettings && (
							<Tabs.Item
								title={t('settings.organization.ai.title')}
								path="/settings/organization/ai"
							/>
						)}
					</Tabs.Container>
				}
			/>
			<Switch>
				<Route path="/settings/organization/departments/new">
					<CreateDepartment />
				</Route>
				<Route path="/settings/organization/departments/:id">
					{({ match }) => <Department id={match!.params.id!} />}
				</Route>
				<Route>
					<Container pt={`${tokens.gap.page}px`} pb={4}>
						<Switch>
							<Route path={['/settings/organization/departments']} exact>
								<OrganizationDepartments />
							</Route>
							<Route path="/settings/organization/teams" exact>
								<SettingsTeams />
							</Route>
							<Route path="/settings/organization/teams/new" exact>
								<SettingsCreateNewTeam />
							</Route>
							<Route path="/settings/organization/teams/:id" exact>
								{({ match }) => <SettingsEditTeamID id={match!.params.id!} />}
							</Route>
							{canViewAndEditAllSettings && (
								<Route path="/settings/organization/theme">
									<SettingsThemeContainer />
								</Route>
							)}
							{(canViewAndEditAllSettings || isHRBusinessPartner) && hasGoals && (
								<Route path="/settings/organization/drivers">
									<GoalDriverSettingsContainer />
								</Route>
							)}
							{canViewAndEditAllSettings && (
								<Route path="/settings/organization/ai">
									<AISettingsContainer />
								</Route>
							)}
							<Route>
								<Redirect to="/settings/organization/departments" />
							</Route>
						</Switch>
					</Container>
				</Route>
			</Switch>
		</>
	);
}
